import { apiService } from './api'

export interface User {
  _id: string
  email: string
  username?: string
  firstName?: string
  lastName?: string
  phone?: string
  avatar?: string
  walletAddress?: string
  authType: 'email' | 'wallet' | 'social'
  status: 'active' | 'inactive' | 'suspended' | 'pending'
  emailVerified: boolean
  phoneVerified: boolean
  preferences: {
    theme: 'light' | 'dark' | 'auto'
    language: string
    notifications: {
      email: boolean
      push: boolean
      sms: boolean
      marketing: boolean
    }
    privacy: {
      profileVisible: boolean
      activityVisible: boolean
      dataSharing: boolean
    }
  }
  web3Profile?: {
    did?: string
    ensName?: string
    nftAvatar?: string
    socialLinks?: Array<{
      platform: string
      url: string
    }>
    badges?: Array<{
      name: string
      description: string
      earnedAt: string
    }>
  }
  subscription: {
    plan: 'free' | 'basic' | 'premium' | 'enterprise'
    startDate?: string
    endDate?: string
    autoRenew: boolean
  }
  lastLogin?: string
  loginCount: number
  createdAt: string
  updatedAt: string
}

export interface LoginCredentials {
  email: string
  password: string
  rememberMe?: boolean
}

export interface RegisterData {
  email: string
  password: string
  firstName?: string
  lastName?: string
  phone?: string
  preferences?: Partial<User['preferences']>
}

export interface WalletLoginData {
  walletAddress: string
  signature: string
  message: string
}

export interface AuthResponse {
  success: boolean
  message: string
  data: {
    user: User
    token: string
    expiresIn?: string
    requiresVerification?: boolean
  }
}

class AuthService {
  private token: string | null = null
  private user: User | null = null

  constructor() {
    // 从localStorage恢复token
    this.token = localStorage.getItem('auth_token')
    this.user = this.getStoredUser()
  }

  // 获取存储的用户信息
  private getStoredUser(): User | null {
    try {
      const userStr = localStorage.getItem('user')
      return userStr ? JSON.parse(userStr) : null
    } catch {
      return null
    }
  }

  // 存储用户信息
  private setStoredUser(user: User | null) {
    if (user) {
      localStorage.setItem('user', JSON.stringify(user))
    } else {
      localStorage.removeItem('user')
    }
  }

  // 存储token
  private setToken(token: string | null) {
    if (token) {
      localStorage.setItem('auth_token', token)
      this.token = token
    } else {
      localStorage.removeItem('auth_token')
      this.token = null
    }
  }

  // 获取当前用户（本地）
  getCurrentUserLocal(): User | null {
    return this.user
  }

  // 获取token
  getToken(): string | null {
    return this.token
  }

  // 检查是否已登录
  isAuthenticated(): boolean {
    return !!(this.token && this.user)
  }

  // 检查用户状态
  isUserActive(): boolean {
    return this.user?.status === 'active'
  }

  // 检查邮箱是否已验证
  isEmailVerified(): boolean {
    return this.user?.emailVerified || false
  }

  // 检查是否连接了钱包
  hasWallet(): boolean {
    return !!(this.user?.walletAddress)
  }

  // 邮箱登录
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      const response = await apiService.post('/auth/login', credentials)
      
      if (response.data.success) {
        const { user, token } = response.data.data
        this.setToken(token)
        this.setStoredUser(user)
        this.user = user
      }
      
      return response.data
    } catch (error: any) {
      throw new Error(error.response?.data?.message || '登录失败')
    }
  }

  // 检查钱包地址是否绑定设备
  async checkDeviceBinding(walletAddress: string): Promise<boolean> {
    try {
      const response = await fetch(`https://airdrop.coretrust.cc/api/airdrop/addressCheck?address=${walletAddress}`)
      const data = await response.json()
      
      // 假设接口返回 { success: true, bound: true/false } 或类似结构
      // 根据实际API响应结构调整
      return data.bound === true || data.success === true
    } catch (error) {
      console.error('检查设备绑定失败:', error)
      return false
    }
  }

  // 钱包登录
  async walletLogin(data: WalletLoginData): Promise<AuthResponse> {
    try {
      // 先检查设备绑定
      const isDeviceBound = await this.checkDeviceBinding(data.walletAddress)
      
      if (!isDeviceBound) {
        throw new Error('该钱包地址未绑定CoreTrust设备，无法登录')
      }

      const response = await apiService.post('/auth/wallet-login', data)
      
      if (response.data.success) {
        const { user, token } = response.data.data
        this.setToken(token)
        this.setStoredUser(user)
        this.user = user
      }
      
      return response.data
    } catch (error: any) {
      throw new Error(error.message || error.response?.data?.message || '钱包登录失败')
    }
  }

  // 注册
  async register(data: RegisterData): Promise<AuthResponse> {
    try {
      const response = await apiService.post('/auth/register', data)
      
      if (response.data.success) {
        const { user, token } = response.data.data
        this.setToken(token)
        this.setStoredUser(user)
        this.user = user
      }
      
      return response.data
    } catch (error: any) {
      throw new Error(error.response?.data?.message || '注册失败')
    }
  }

  // 登出
  async logout(): Promise<void> {
    try {
      if (this.token) {
        await apiService.post('/auth/logout', {}, {
          headers: { Authorization: `Bearer ${this.token}` }
        })
      }
    } catch (error) {
      console.error('登出请求失败:', error)
    } finally {
      this.setToken(null)
      this.setStoredUser(null)
      this.user = null
    }
  }

  // 获取用户信息
  async getCurrentUser(): Promise<User> {
    try {
      const response = await apiService.get('/auth/me', {
        headers: { Authorization: `Bearer ${this.token}` }
      })
      
      if (response.data.success) {
        const user = response.data.data
        this.setStoredUser(user)
        this.user = user
        return user
      }
      
      throw new Error('获取用户信息失败')
    } catch (error: any) {
      throw new Error(error.response?.data?.message || '获取用户信息失败')
    }
  }

  // 更新用户信息
  async updateUser(data: Partial<User>): Promise<User> {
    try {
      const response = await apiService.put('/auth/me', data, {
        headers: { Authorization: `Bearer ${this.token}` }
      })
      
      if (response.data.success) {
        const user = response.data.data
        this.setStoredUser(user)
        this.user = user
        return user
      }
      
      throw new Error('更新用户信息失败')
    } catch (error: any) {
      throw new Error(error.response?.data?.message || '更新用户信息失败')
    }
  }

  // 修改密码
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    try {
      const response = await apiService.put('/auth/change-password', {
        currentPassword,
        newPassword
      }, {
        headers: { Authorization: `Bearer ${this.token}` }
      })
      
      if (!response.data.success) {
        throw new Error(response.data.message)
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || '修改密码失败')
    }
  }

  // 刷新token
  async refreshToken(): Promise<string> {
    try {
      const response = await apiService.post('/auth/refresh', {}, {
        headers: { Authorization: `Bearer ${this.token}` }
      })
      
      if (response.data.success) {
        const { token } = response.data.data
        this.setToken(token)
        return token
      }
      
      throw new Error('刷新token失败')
    } catch (error: any) {
      throw new Error(error.response?.data?.message || '刷新token失败')
    }
  }

  // 获取认证头
  getAuthHeaders(): Record<string, string> {
    return this.token ? { Authorization: `Bearer ${this.token}` } : {}
  }

  // 检查token是否过期
  isTokenExpired(): boolean {
    if (!this.token) return true
    
    try {
      const payload = JSON.parse(atob(this.token.split('.')[1]))
      return Date.now() >= payload.exp * 1000
    } catch {
      return true
    }
  }

  // 自动刷新token
  async autoRefreshToken(): Promise<boolean> {
    if (this.isTokenExpired()) {
      try {
        await this.refreshToken()
        return true
      } catch {
        this.logout()
        return false
      }
    }
    return true
  }
}

export const authService = new AuthService()
export default authService